import { Button } from '@creditornot/cb-button';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { EmptyIcon } from '@creditornot/cb-placeholders';
import { Popover } from '@creditornot/cb-popover';
import { Select } from '@creditornot/cb-select';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { createMenu } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { Typography as T } from 'common/typography';
import { languages } from 'config';

import useMenuData from '../useMenuData';

type Props = {
  defaultLanguage: Language | undefined;
  hasCreateMenuPermission: boolean;
  venueId: string;
  venueSlug: string;
};

const languageLabels: { [key in Language]: string } = {
  ar: 'languages.ar',
  'ar-IL': 'languages.ar-IL',
  az: 'languages.az',
  cs: 'languages.cs',
  da: 'languages.da',
  de: 'languages.de',
  ee: 'languages.ee',
  el: 'languages.el',
  en: 'languages.en',
  et: 'languages.et',
  fi: 'languages.fi',
  fr: 'languages.fr',
  he: 'languages.he',
  hr: 'languages.hr',
  hu: 'languages.hu',
  is: 'languages.is',
  ja: 'languages.ja',
  ka: 'languages.ka',
  lt: 'languages.lt',
  lv: 'languages.lv',
  nb: 'languages.nb',
  pl: 'languages.pl',
  ru: 'languages.ru',
  sk: 'languages.sk',
  sl: 'languages.sl',
  sq: 'languages.sq',
  sr: 'languages.sr',
  sv: 'languages.sv',
  uz: 'languages.uz',
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  align-items: center;
  background-color: ${cbSemanticCssVars.surfaceMain};
  padding-block-start: 64px;
`;

const MenuIcon = styled(EmptyIcon.FileText)`
  margin-block-end: 16px;
`;

const Title = styled(T.Body2)`
  font-weight: bold;
  color: ${cbSemanticCssVars.textSecondary};
  margin-block-end: 8px;
`;

const SubTitle = styled(T.Small)`
  color: ${cbSemanticCssVars.textSecondary};
  margin-block-end: 32px;
`;

const LanguageSelect = styled(Select)`
  width: 240px;
  margin-block-end: 16px;
`;

const CreateButton = styled(Button).attrs({ size: 'small', variant: 'blue' })``;

const NoPermission = styled.div`
  max-width: 300px;
  padding: 10px;
`;

const CreateMenuForm = ({ defaultLanguage, hasCreateMenuPermission, venueId, venueSlug }: Props) => {
  const queryClient = useQueryClient();
  const [language, setLanguage] = useState<Language | undefined>(defaultLanguage);
  const { updateMenuData } = useMenuData();

  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });

  const options = languages.map(lang => ({ value: lang, label: t(languageLabels[lang]) }));

  const { mutate, isLoading } = useMutation(async (language: Language) => createMenu(language, venueId), {
    onSuccess: menu => {
      queryClient.invalidateQueries(['/v1/merchant-admin/venues/', venueSlug]);
      updateMenuData({ actionType: 'updateMenuContainer', payload: { menu, items: [], options: [] } });
    },
    onError: () => {
      toast.error(<FormattedMessage id="errors.unknown-error-retry-contact" />, { duration: Infinity });
    },
  });

  return (
    <Root>
      <MenuIcon />
      <Title>{t('menu.noMenuForVenue')}</Title>
      <SubTitle>{t('menu.createEmptyMenuInfo')}</SubTitle>
      <LanguageSelect
        disabled={isLoading}
        value={language}
        options={options}
        clearable={false}
        placeholder={t('menu.selectLanguage')}
        onChange={(lang: Language) => setLanguage(lang)}
      />
      <Popover
        disabled={hasCreateMenuPermission}
        content={<NoPermission>{t('menu.no-create-menu-permission')}</NoPermission>}
        placement="bottom"
        renderArrow
      >
        <CreateButton
          disabled={language === undefined || isLoading || !hasCreateMenuPermission}
          onClick={() => language !== undefined && mutate(language)}
          loading={isLoading}
        >
          {t('menu.createEmptyMenu')}
        </CreateButton>
      </Popover>
    </Root>
  );
};

export default CreateMenuForm;
